import React, { useEffect, useState } from "react";
import { value1, value2, value3 } from "../../Assets.tsx";

/* Colours: text #4e5356 | buttons #2596be | headings #4573a6
   Fonts  : Ubuntu Bold (headings) / Open Sans Regular (body)
   Idea   : value cards drop in one after another; on hover the card fills with a
            gradient from the top, a light sweeps down and the heading changes colour. */

const valuesData = [
  {
    id: 1,
    title: "Excellence",
    description:
      "We are committed to achieving the highest standards of quality, fostering a culture of merit and continuous improvement.",
    imgSrc: value1,
    alt: "value1",
  },
  {
    id: 2,
    title: "Pioneering",
    description:
      "We embrace boldness and agility, fearlessly tackling challenges to deliver innovative solutions for the digital world.",
    imgSrc: value2,
    alt: "value2",
  },
  {
    id: 3,
    title: "Collaborative Mindset",
    description:
      "We prioritize our people and partners, fostering continuous learning and nurturing collaborative relationships built on trust and mutual respect.",
    imgSrc: value3,
    alt: "value3",
  },
];

const STYLES = `
@import url("https://fonts.googleapis.com/css2?family=Open+Sans:wght@400&family=Ubuntu:wght@700&display=swap");
.ov {
  --text: #4e5356;
  --head: #4573a6;
  --btn: #2596be;
  --line: #c9dde8;
  --g: 28px;

  position: relative;
  overflow: hidden;
  padding: 90px 24px 100px;
  font-family: "Open Sans", sans-serif;
  font-weight: 400;
  color: var(--text);
  background: #ffffff;
}
.ov::before {
  content: none;
}
  .ov>*{position:relative}

.ov-head{max-width:820px;margin:0 auto 50px;text-align:center}
.ov-head h2{font-family:"Ubuntu",sans-serif;font-weight:700;font-size:44px;line-height:1.15;margin:0 0 16px;color:var(--head);cursor:default;transition:color .35s,letter-spacing .35s}
.ov-head h2:hover{color:var(--btn);letter-spacing:.6px}
.ov-head p{font-size:17px;line-height:1.8;margin:0;color:var(--text)}

.ov-grid{position:relative;max-width:1180px;margin:0 auto;display:grid;grid-template-columns:repeat(3,1fr);gap:var(--g);align-items:stretch}

.ov-card{position:relative;overflow:hidden;border:1px solid var(--line);border-radius:22px;padding:34px 28px 30px;text-align:center;
  background:linear-gradient(180deg,#4573a6,#2596be) no-repeat top/100% 0,#fff;box-shadow:0 20px 40px -26px rgba(69,115,166,.55);
  transition:background-size .55s cubic-bezier(.2,.8,.2,1),transform .4s cubic-bezier(.3,1.4,.5,1),box-shadow .4s,border-color .4s}
.ov-card::after{content:"";position:absolute;left:0;right:0;top:-100%;height:55%;pointer-events:none;background:linear-gradient(180deg,transparent,rgba(255,255,255,.3),transparent)}
.ov-card:hover{background-size:100% 100%,auto;transform:translateY(-10px) scale(1.02);border-color:transparent;box-shadow:0 34px 56px -22px rgba(37,150,190,.7)}
.ov-card:hover::after{animation:ovSweep .9s ease}
.ov-card h3,.ov-card p{transition:color .35s}
.ov-card:hover h3,.ov-card:hover p{color:#fff}
.ov-card:hover .ov-avatar{box-shadow:0 0 0 6px rgba(255,255,255,.35),0 14px 28px -8px rgba(0,0,0,.35);animation-duration:3s}
.ov-card:hover .ov-avatar img{animation-duration:3s}

.ov-avatar{transition:box-shadow .4s;position:relative;width:104px;height:104px;margin:0 auto 22px;border-radius:50%;padding:5px;
  background:conic-gradient(from 0deg,var(--btn),var(--head),#bfe3f1,var(--btn));animation:ovSpin 8s linear infinite}
.ov-avatar img{display:block;width:100%;height:100%;object-fit:cover;border-radius:50%;border:4px solid #fff;background:#fff;animation:ovSpin 8s linear infinite reverse}
.ov-card h3{font-family:"Ubuntu",sans-serif;font-weight:700;font-size:22px;margin:0 0 12px;color:var(--head)}
.ov-card p{font-size:15.5px;line-height:1.75;margin:0;color:var(--text)}

/* middle card is highlighted (same height and position as the others) */
.ov-card.feat{background:linear-gradient(180deg,#4573a6,#2596be);border-color:transparent;box-shadow:0 30px 50px -24px rgba(37,150,190,.7)}
.ov-card.feat h3,.ov-card.feat p{color:#fff}
.ov-card.feat .ov-avatar{background:conic-gradient(from 0deg,#fff,#bfe3f1,#fff,#8fd0e8,#fff)}

/* reveal: lines draw downward, cards drop in one after another */
.ov-head,.ov-card{opacity:0}
.ov.in .ov-head,.ov.in .ov-card{opacity:1}
.ov.in .ov-head{animation:ovDrop .9s cubic-bezier(.2,.8,.2,1) backwards}
.ov.in .ov-card{animation:ovDrop .9s cubic-bezier(.3,1.2,.5,1) backwards}
.ov.in .ov-card:nth-child(1){animation-delay:.6s}
.ov.in .ov-card:nth-child(2){animation-delay:.8s}
.ov.in .ov-card:nth-child(3){animation-delay:1s}
@keyframes ovDrop{from{opacity:0;transform:translateY(-70px)}}
@keyframes ovSweep{from{top:-100%}to{top:160%}}
@keyframes ovSpin{to{transform:rotate(360deg)}}

@media(max-width:900px){
  .ov{padding:70px 20px 80px}
  .ov-head h2{font-size:34px}
  .ov-grid{grid-template-columns:1fr;gap:24px}
}
@media(prefers-reduced-motion:reduce){
  .ov *,.ov *::before,.ov *::after{animation:none!important;transition:none!important}
  .ov .ov-head,.ov .ov-card{opacity:1}
}
`;

const OurValues = () => {
  const [shown, setShown] = useState(false);

  // start the animation when the section scrolls into view
  useEffect(() => {
    const el = document.getElementById("our-values-root");
    if (!el || !("IntersectionObserver" in window)) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div id="our-values-root" className={"ov" + (shown ? " in" : "")}>
      <style>{STYLES}</style>

      <div className="ov-head">
        <h2>Our Values</h2>
        <p>
          We provide a comprehensive range of services designed to deliver optimal solutions in strategy, consulting,
          and digital technologies. As a forward-thinking company specializing in innovation, we’ve built strategic
          partnerships to help clients navigate the modern digital world.
        </p>
      </div>

      <div className="ov-grid">
        {valuesData.map((value, index) => (
          <div className={"ov-card" + (index === 1 ? " feat" : "")} key={value.id}>
            <div className="ov-avatar">
              <img src={value.imgSrc} alt={value.alt} />
            </div>
            <h3>{value.title}</h3>
            <p>{value.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OurValues;